import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TOKEN, GUEST, useStore } from '../store';
import { guestLogin } from '../actions/UserActions';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserQuery } from '../actions/UserActions';
import { usePricingQuery } from '../actions/WorkspaceActions';
import { useCreditsQuery } from '../actions/CreditsActions';
import { useIsMobile } from '../utils/hooks';
import { IS_SINGLE_TENENT_MODE } from '../constants';
import { isTrialEnded } from '../utils/trial';

import { useReportUserLastSeen } from '../actions/UserActions';

const INTERCOM_API_BASE = process.env.REACT_APP_INTERCOM_API_BASE;
const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;

export default function LoggedInWrapper({
  children,
  requireSales = false,
  requireRole = null,
  allowGuests = false,
  allowTrialEnded = false,
}) {
  const [token, setToken] = useStore(TOKEN);
  const [_, setIsGuest] = useStore(GUEST);
  const { isMobile } = useIsMobile();
  const { i18n } = useTranslation();
  const { user, isLoading } = useUserQuery();
  const location = useLocation();

  // Preload pricing data to avoid flicker
  // eslint-disable-next-line no-unused-vars
  const { pricing } = usePricingQuery({ enabled: !!user?.id });
  const { credits } = useCreditsQuery({ enabled: !!user?.id });

  useReportUserLastSeen();

  useEffect(() => {
    (async () => {
      if (!token && allowGuests) {
        const res = await guestLogin();
        setToken(res?.token);
        setIsGuest(true);
      }
    })();
  }, [token, allowGuests, setToken, setIsGuest]);

  useEffect(() => {
    if (
      !IS_SINGLE_TENENT_MODE &&
      user &&
      user.id &&
      credits &&
      INTERCOM_API_BASE &&
      INTERCOM_APP_ID &&
      user?.workspace?.type &&
      window.Intercom
    ) {
      window.Intercom('boot', {
        api_base: INTERCOM_API_BASE,
        app_id: INTERCOM_APP_ID,
        user_id: user.id,
        type: user.workspace.type,
        created_at: user.workspace.created_at,
        newsletter: user.newsletter || false,
        email_domain_name: '@' + user.email.split('@')?.[1],
        email_verified: !!user.email_validated_at,
        calendar_sync_join_behavior: user.calendar_sync_join_behavior,
        default_model: user.default_model,
        has_two_factor: user.has_two_factor,
        seat_limit: user.workspace?.seat_limit,
        analysis_credits: credits?.generation?.credits,
        export_credits: credits?.export?.credits,
        used_seats: user.workspace?.used_seats,
        alignment: 'left',
        language_override: i18n.language,
      });

      if (isMobile) {
        window.Intercom('update', { hide_default_launcher: true });
      }
    }

    if (user && user?.language) {
      i18n.changeLanguage(user.language);
    }
  }, [user, i18n, isMobile, credits]);

  if (!token && allowGuests) {
    return null;
  }

  if (!token || (user.role === 'guest' && !allowGuests)) {
    return <Navigate to="/login" state={{ redirectUrl: location }} />;
  } else if (
    !isLoading &&
    requireSales &&
    user?.role !== 'sales' &&
    user?.role !== 'admin'
  ) {
    return <Navigate to="/" />;
  } else if (
    !isLoading &&
    requireRole && 
    user?.role !== requireRole
  ) {
    return <Navigate to="/" />;
  } else if (!isLoading && isTrialEnded(user) && !allowTrialEnded) {
    return <Navigate to="/trial-ended" />;
  } else if (isLoading) {
    return null;
  } else {
    return children;
  }
}
